import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { Modal as ModalContainer, Box, useTheme } from '@mui/material';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { useTranslation } from 'react-i18next';
import { useContext, useState, useEffect } from 'react';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { TipsHistory } from '@/services/PaymentsServiceV2';
import { DATE_FORMAT } from '@/lib/constants';
import { useFormatting } from '@/utils/formatting';
import { SMMedium, SMSemiBold, SubTitle } from '@/typography/Typography';
import { UserContext } from '@/contexts/user/UserContext';
import { InputText } from '@/components/shared/InputText';
import {
    PrimaryButtonLG,
    SecondaryButtonGrayLG
} from '@/components/shared/Button';
import { isValidNumber } from '@/utils/validationHelpers';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { Dropdown, DropDownChoices } from '@/components/shared/Dropdown';

interface EditTipsDialogProp {
    editTip: TipsHistory | null;
    onClose?: () => void;
}

export const EditTipsDialog = ({ editTip, onClose }: EditTipsDialogProp) => {
    const { t } = useTranslation();

    const { updateJournalAmount } = usePaymentsMutations();

    const [newAmount, setNewAmount] = useState<number | undefined>();
    const [inputValue, setInputValue] = useState<string>('');
    const [selectedJournalId, setSelectedJournalId] = useState<string>('');

    // Check journal counts
    const hasJournalAmounts =
        editTip?.posJournalAmounts && Array.isArray(editTip.posJournalAmounts);
    const hasSingleJournal =
        hasJournalAmounts && editTip.posJournalAmounts!.length === 1;
    const hasMultipleJournals =
        hasJournalAmounts && editTip.posJournalAmounts!.length > 1;

    // Create dropdown choices for journals
    const journalChoices: DropDownChoices<string>[] = hasMultipleJournals
        ? editTip.posJournalAmounts!.map((journal, index) => ({
              label: `${t('tips.journal')} ${index + 1}`,
              value: journal.posJournalId
          }))
        : [];

    // Auto-select first journal if only one exists
    useEffect(() => {
        if (hasSingleJournal) {
            const journal = editTip.posJournalAmounts![0];
            setSelectedJournalId(journal.posJournalId);
        } else if (hasMultipleJournals) {
            // Reset when showing dropdown
            setSelectedJournalId('');
            setNewAmount(undefined);
            setInputValue('');
        }
    }, [editTip, hasSingleJournal, hasMultipleJournals]);

    // Handle journal selection from dropdown
    const handleJournalChange = (journalId: string) => {
        setSelectedJournalId(journalId);
        updateJournalAmount.reset();
    };

    const onCloseHandler = () => {
        setNewAmount(undefined);
        setInputValue('');
        setSelectedJournalId('');
        updateJournalAmount.reset();
        onClose?.();
    };

    const { getBalanceLabel } = useFormatting();
    const { company } = useContext(UserContext);

    useHandleResponse({
        error: null, // handled outside
        isSuccess: updateJournalAmount.isSuccess,
        successAlertMessage: t('tips.redistribution_successful'),
        isSuccessCallBack: () => {
            onCloseHandler();
        }
    });

    const theme = useTheme();

    return (
        <ModalContainer open={!!editTip} onClose={onCloseHandler}>
            <ConfirmationBox style={{ width: '450px' }}>
                <ConnectHeader
                    // logo={getLogoForPOSProvider()}
                    title={t('tips.edit_tips_for')}
                    description={t('tips.edit_tips_for_description', {
                        date: editTip?.date?.format(
                            DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                        ),
                        locationName: editTip?.locationName
                    })}
                />

                <SMMedium style={{ textAlign: 'center' }} mb={3} mt={2}>
                    {t('tips.current_tips_amount_label')}{' '}
                    {getBalanceLabel(editTip?.totalTips, company?.currency)}
                </SMMedium>

                {hasMultipleJournals && (
                    <Box mb={2}>
                        <Dropdown<string>
                            label={t('tips.select_journal')}
                            choices={journalChoices}
                            value={selectedJournalId}
                            onChange={handleJournalChange}
                        />
                    </Box>
                )}

                <InputText
                    error={updateJournalAmount.isError}
                    value={inputValue}
                    onChange={(e) => {
                        const value = e.target.value;
                        setInputValue(value);

                        if (value === '' || value === '-') {
                            setNewAmount(undefined);
                        } else if (isValidNumber(value)) {
                            setNewAmount(parseInt(value));
                        } else {
                            setNewAmount(undefined);
                        }
                    }}
                    label={t('tips.new_tips_amount')}
                />
                {updateJournalAmount.error ? (
                    <SubTitle color={'error'}>
                        {t('tips.error_cant_distribute')}
                    </SubTitle>
                ) : (
                    <SubTitle>{t('tips.edit_pay')}</SubTitle>
                )}
                <Box
                    m={'20px 0'}
                    display={'flex'}
                    justifyContent={'space-between'}
                >
                    <SecondaryButtonGrayLG
                        onClick={onCloseHandler}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('cancel')}</SMSemiBold>
                    </SecondaryButtonGrayLG>

                    <PrimaryButtonLG
                        loading={updateJournalAmount.isPending}
                        disabled={
                            newAmount === undefined ||
                            newAmount === 0 ||
                            updateJournalAmount.isError ||
                            (hasMultipleJournals && !selectedJournalId)
                        }
                        onClick={() => {
                            const journalId = hasMultipleJournals
                                ? selectedJournalId
                                : editTip?.posJournalId;
                            updateJournalAmount.mutate({
                                posJournalId: journalId as string,
                                newTotalAmount: newAmount!.toString()
                            });
                        }}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('tips.redistribute')}</SMSemiBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
